import React, { useState, useEffect } from 'react';

interface Invitee {
  id: string;
  name1?: string;
  name2?: string;
  name3?: string;
  name4?: string;
  name5?: string;
  name1_rsvp?: boolean;
  name2_rsvp?: boolean;
  name3_rsvp?: boolean;
  name4_rsvp?: boolean;
  name5_rsvp?: boolean;
  message?: string;
  dietaryRestrictions?: string;
}

const ShowAllRsvps: React.FC = () => {
  const [invitees, setInvitees] = useState<Invitee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRsvps = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const id = urlParams.get('id');

        if (!id) {
          setError('No invitation ID provided. Please include an ID in the URL (e.g., /all-rsvps?id=your-id)');
          setIsLoading(false);
          return;
        }

        const response = await fetch(`https://plxzfm4mxf.execute-api.us-east-1.amazonaws.com/default/UpdateBirthdayRsvpTable?id=${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch RSVPs');
        }
        const data = await response.json();
        setInvitees(data);
      } catch (err) {
        setError('Error loading RSVPs');
        console.error('Error fetching RSVPs:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRsvps();
  }, []);

  const calculateStats = () => {
    let yesCount = 0;
    let noCount = 0;
    let pendingCount = 0;
    let totalInvitees = 0;

    invitees.forEach(invitee => {
      for (let i = 1; i <= 5; i++) {
        const nameKey = `name${i}` as keyof Invitee;
        const rsvpKey = `name${i}_rsvp` as keyof Invitee;
        
        if (invitee[nameKey]) {
          totalInvitees++;
          if (invitee[rsvpKey] === true) {
            yesCount++;
          } else if (invitee[rsvpKey] === false) {
            noCount++;
          } else {
            pendingCount++;
          }
        }
      }
    });

    return { yesCount, noCount, pendingCount, totalInvitees };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xl text-gray-600">Loading RSVPs...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xl text-red-600">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  const stats = calculateStats();

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="text-center mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">RSVPs</h1>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            <div className="bg-white rounded-lg shadow p-3 sm:p-6">
              <div className="text-xl sm:text-2xl font-bold text-green-600">{stats.yesCount}</div>
              <div className="text-xs sm:text-sm text-gray-500">Yes</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-6">
              <div className="text-xl sm:text-2xl font-bold text-red-600">{stats.noCount}</div>
              <div className="text-xs sm:text-sm text-gray-500">No</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-6">
              <div className="text-xl sm:text-2xl font-bold text-yellow-600">{stats.pendingCount}</div>
              <div className="text-xs sm:text-sm text-gray-500">Pending</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-6">
              <div className="text-xl sm:text-2xl font-bold text-blue-600">{stats.totalInvitees}</div>
              <div className="text-xs sm:text-sm text-gray-500">Total</div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  <th scope="col" className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dietary
                  </th>
                  <th scope="col" className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invite
                  </th>
                  <th scope="col" className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:hidden">
                    Send
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitees.map(invitee => {
                  // Get all valid names for this family
                  const familyNames = Array.from({length: 5}, (_, i) => i + 1)
                    .map(i => {
                      const nameKey = `name${i}` as keyof Invitee;
                      const name = invitee[nameKey];
                      return name ? { 
                        name,
                        nameKey,
                        rsvpKey: `name${i}_rsvp` as keyof Invitee,
                      } : null;
                    })
                    .filter((item): item is NonNullable<typeof item> => item !== null);

                  if (familyNames.length === 0) return null;

                  return (
                    <React.Fragment key={invitee.id}>
                      <tr className="h-4 bg-gray-50">
                        <td colSpan={5} className="border-t border-b border-gray-200"></td>
                      </tr>
                      {familyNames.map(({name, nameKey, rsvpKey}, index) => (
                        <tr key={`${invitee.id}-${nameKey}`} className="group-hover:bg-gray-50">
                          <td className="px-4 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-b border-gray-100">
                            {name}
                          </td>
                          <td className="px-4 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm border-b border-gray-100">
                            {invitee[rsvpKey] === true ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Yes
                              </span>
                            ) : invitee[rsvpKey] === false ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                No
                              </span>
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                ?
                              </span>
                            )}
                          </td>
                          {index === 0 && (
                            <>
                              <td rowSpan={familyNames.length} className={`px-4 sm:px-6 py-2 sm:py-4 text-sm text-gray-500 border-b border-gray-100 ${familyNames.length > 1 ? 'align-middle' : 'align-top'}`}>
                                {invitee.message || '-'}
                              </td>
                              <td rowSpan={familyNames.length} className={`px-4 sm:px-6 py-2 sm:py-4 text-sm text-gray-500 border-b border-gray-100 ${familyNames.length > 1 ? 'align-middle' : 'align-top'}`}>
                                {invitee.dietaryRestrictions || '-'}
                              </td>
                              <td rowSpan={familyNames.length} className={`px-4 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm border-b border-gray-100 ${familyNames.length > 1 ? 'align-middle' : 'align-top'}`}>
                                <a
                                  href={`/?id=${invitee.id}`}
                                  className="text-sky-700 hover:text-sky-800 font-medium hover:underline text-xs sm:text-sm"
                                >
                                  View
                                </a>
                              </td>
                              <td
                                rowSpan={familyNames.length}
                                className={`px-4 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm border-b border-gray-100 ${familyNames.length > 1 ? 'align-middle' : 'align-top'} sm:hidden`}
                              >
                                <a
                                  href={`sms:?&body=${encodeURIComponent(`You're invited to Daniel’s 2nd birthday party on June 13, 2026 from 10:30am - 1pm at Seattle Children’s Museum! Please RSVP at this website. https://danielslutsker.com/?id=${invitee.id}`)}`}
                                  className="text-blue-600 hover:text-blue-800 font-medium hover:underline text-xs"
                                >
                                  Send
                                </a>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShowAllRsvps; 